import { api } from '@/lib/api'
import { SERVICE_WORKER_URL } from '@/lib/constants'

/** Registra el service worker (caché del shell, lecturas sin conexión y Web Push). */
export function registerServiceWorker(): void {
  if (!('serviceWorker' in navigator)) return
  window.addEventListener('load', () => {
    navigator.serviceWorker.register(SERVICE_WORKER_URL).catch((error: unknown) => {
      console.error('No se pudo registrar el service worker', error)
    })
  })
}

/** Estado de las notificaciones push en este dispositivo. */
export type PushStatus =
  /** El navegador no soporta Web Push (p. ej. iOS sin instalar la app). */
  | 'unsupported'
  /** La institución no configuró llave VAPID o apagó las notificaciones push. */
  | 'unavailable'
  /** El usuario bloqueó las notificaciones en el navegador. */
  | 'denied'
  | 'subscribed'
  | 'unsubscribed'

/** ¿El navegador soporta service workers y Push API? */
export function isPushSupported(): boolean {
  return typeof window !== 'undefined' && 'serviceWorker' in navigator && 'PushManager' in window && 'Notification' in window
}

/** Llave VAPID en base64url → bytes, como exige `pushManager.subscribe`. */
export function urlBase64ToUint8Array(base64: string): Uint8Array {
  const BASE64_BLOCK = 4
  const padding = '='.repeat((BASE64_BLOCK - (base64.length % BASE64_BLOCK)) % BASE64_BLOCK)
  const normalized = (base64 + padding).replace(/-/g, '+').replace(/_/g, '/')
  const raw = atob(normalized)
  return Uint8Array.from(raw, (char) => char.charCodeAt(0))
}

async function readyRegistration(): Promise<ServiceWorkerRegistration | null> {
  if (!isPushSupported()) return null
  const registration = await navigator.serviceWorker.getRegistration()
  return registration ? navigator.serviceWorker.ready : null
}

/** Estado actual de la suscripción push de este dispositivo. */
export async function getPushStatus(vapidPublicKey: string | null): Promise<PushStatus> {
  if (!isPushSupported()) return 'unsupported'
  if (!vapidPublicKey) return 'unavailable'
  if (Notification.permission === 'denied') return 'denied'
  const registration = await readyRegistration()
  if (!registration) return 'unavailable'
  const subscription = await registration.pushManager.getSubscription()
  return subscription ? 'subscribed' : 'unsubscribed'
}

/**
 * Pide permiso, suscribe el dispositivo con la llave VAPID de la institución
 * y lo registra en el API (`POST /notifications/push-subscriptions`).
 */
export async function subscribeToPush(vapidPublicKey: string): Promise<PushStatus> {
  const permission = await Notification.requestPermission()
  if (permission !== 'granted') return permission === 'denied' ? 'denied' : 'unsubscribed'
  const registration = await readyRegistration()
  if (!registration) return 'unavailable'
  const subscription =
    (await registration.pushManager.getSubscription()) ??
    (await registration.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(vapidPublicKey) as BufferSource,
    }))
  const json = subscription.toJSON()
  await api.post('/notifications/push-subscriptions', { endpoint: json.endpoint, keys: json.keys })
  return 'subscribed'
}

/** Anula la suscripción de este dispositivo en el navegador y en el API. */
export async function unsubscribeFromPush(): Promise<PushStatus> {
  const registration = await readyRegistration()
  const subscription = await registration?.pushManager.getSubscription()
  if (subscription) {
    await api.delete('/notifications/push-subscriptions', { endpoint: subscription.endpoint })
    await subscription.unsubscribe()
  }
  return 'unsubscribed'
}
